import { setApiConfig } from "../@api/config";
import { setSessionError } from "../store/sessionError";
import { updateRequestConfig } from "@kivra/sdk/common";
import { setPageLanguage } from "@kivra/sdk/copy-consumer";
import { history, store } from "../store/configureStore";
import { getColorScheme } from "../utils/prefersColorScheme";
import { initTunnistus } from "../utils/teliaTunnistus";
import {
  getPathname,
  isPasswordResetReturnPath,
  isSignupAfterStrongAuthPath,
  isTunnistusReturnPath,
} from "../utils/location";
import { routes } from "../utils/routes";
import { setTraceId } from "../store/auth";
import type { AppProps } from "../types";
import {
  setDeviceType,
  setHideNavigation,
} from "../store/hideNavigation/hideNavigation";
import { getConfig } from "./config";
import { fetchLangJson } from "./copy";
import { getUrlParameters } from "./urlParameters";
import { handleStrongAuthReturn } from "./strongAuthReturn";
import { initReactSentry } from "@kivra/sdk/log/sentry-react";

export async function bootstrap(): Promise<AppProps> {
  const [config, { lang, initLang }] = await Promise.all([
    getConfig(),
    fetchLangJson(),
  ]);
  const location = window.location;
  const urlParameters = getUrlParameters(location, config);

  // This feels a bit silly but seems to be necessary
  // due to certain modules inherited from Sweden
  updateRequestConfig({
    kivraCoreOrigin: config.core_api_host,
  });

  setPageLanguage(initLang);
  initReactSentry({
    dsn: config.sentry_dsn_kivra_auth,
    enabled: import.meta.env.MODE !== "development",
    release: import.meta.env.VITE_APP_VERSION ?? "prerelease",
  });
  setApiConfig(config, urlParameters.redirectParameters);
  initTunnistus(config);

  const scheme = urlParameters.colorScheme ||
    getColorScheme(config, window.location.href);

  setSessionError(urlParameters.auto_logout === true);
  if (document.cookie.includes("kv.session")) {
    document.cookie =
      "kv.session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; samesite=none; domain=.kivra.fi; secure";
  }
  setHideNavigation(urlParameters.hideNavigation);
  setDeviceType(urlParameters.device);
  // clear url parameters
  const path = getPathname(location);
  history.replace(path);

  // don't allow direct navigation to other than /tunnistus/return
  // could have a guard for this, but current might be more explicit
  if (isSignupAfterStrongAuthPath(location)) {
    history.replace(routes.ROOT);
  }
  // handle strong auth return
  let isDirectLoginRedirect = false;
  if (urlParameters.isStrongAuthReturn) {
    if (urlParameters.code) {
      isDirectLoginRedirect = await handleStrongAuthReturn(
        store.dispatch,
        history,
        location,
        urlParameters.code,
        urlParameters.acr_values ?? "",
        urlParameters.termsAccepted ?? false,
      );
    } else {
      const traceId = `traceId: ${urlParameters.traceId}`;
      store.dispatch(setTraceId(traceId));
      const authContext = urlParameters.authContext;
      const allowedReturnPaths = [
        routes.SIGNUP,
        routes.SIGNUP_V2,
        routes.PASSWORD_RESET,
        routes.LOGIN,
      ];
      if (
        (isTunnistusReturnPath(location) ||
          isPasswordResetReturnPath(location)) &&
        authContext &&
        allowedReturnPaths.includes(authContext)
      ) {
        history.replace(authContext);
      } else {
        history.replace(routes.ROOT);
      }
    }
  }
  return {
    config,
    lang,
    store,
    history,
    scheme,
    isDirectLoginRedirect,
    initLang,
  };
}
